import { REVIEW_PASSES, type AccessibleProject, type AdHocRuleInput, type DocumentType, type Issue, type IssueResolution, type Project, type ProjectContext, type QcTemplate, type QcTemplateRule, type RuleSeverity, type Form, type FormField, type FormResponses, type ResolutionMethod, type ReviewPassName, type ReviewPassState, type RunCollabSnapshot, type RunLiveEvent, type RunParticipant, type SharedRunSummary, type UnitLabel, type UserSearchResult } from '@qcbot/shared';
import { getAccessToken } from './authConfig';

// Trailing slash stripped defensively — a GitHub variable set with one (e.g. ".../net/" instead
// of ".../net") would otherwise double up with each call site's own leading "/api/..." path.
const API_BASE = __API_BASE__.replace(/\/+$/, '');

/** An API failure carrying the server's message and (optional) machine-readable `code`, e.g.
 * 'project_unavailable' when the selected project/template was archived mid-session. */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function toApiError(res: Response, fallback: string): Promise<ApiError> {
  const body = (await res.json().catch(() => undefined)) as { error?: string; code?: string } | undefined;
  return new ApiError(body?.error ?? `${fallback}: ${res.status}`, res.status, body?.code);
}

/** Attaches the signed-in user's Entra ID access token to every request — every backend endpoint
 * the add-in calls is now auth-scoped to "projects/templates this user can access", so there are
 * no more unauthenticated public endpoints. */
async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new ApiError('Not signed in', 401);
  return fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...(init.headers ?? {}), Authorization: `Bearer ${token}` },
  });
}

// ---------------------------------------------------------------------------
// Project + QC Template + Form selection
// ---------------------------------------------------------------------------

/** Every project the signed-in user can access (owned, or via a project/template collaborator
 * grant) — the add-in's flat project picker groups/labels these by workspace owner itself,
 * rather than needing a separate workspace-switcher control. */
export async function fetchMyProjects(): Promise<AccessibleProject[]> {
  const res = await authedFetch('/api/admin/projects/mine');
  if (!res.ok) return [];
  const data = (await res.json()) as { projects: AccessibleProject[] };
  return data.projects;
}

export async function fetchProjectQcTemplates(projectId: string): Promise<QcTemplate[]> {
  const res = await authedFetch(`/api/admin/qc-templates?projectId=${encodeURIComponent(projectId)}`);
  if (!res.ok) return [];
  const data = (await res.json()) as { qcTemplates: QcTemplate[] };
  return data.qcTemplates;
}

/** Quick-onboarding project creation (see components/NewProjectDialog.tsx) — same endpoint/shape
 * as the admin app's CreateProjectDialog, just invoked from the task pane. */
export async function createProject(input: { name: string; description?: string }): Promise<Project> {
  const res = await authedFetch('/api/admin/projects', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) });
  if (!res.ok) throw await toApiError(res, 'Failed to create project');
  const data = (await res.json()) as { project: Project };
  return data.project;
}

/** Quick-onboarding template creation (see components/NewTemplateDialog.tsx). `isDefault` should
 * only be passed true for a project's very first template (callers check that locally). */
export async function createQcTemplate(input: { projectId: string; name: string; isDefault?: boolean; documentTypes?: DocumentType[] }): Promise<QcTemplate> {
  const res = await authedFetch('/api/admin/qc-templates', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) });
  if (!res.ok) throw await toApiError(res, 'Failed to create QC template');
  const data = (await res.json()) as { qcTemplate: QcTemplate };
  return data.qcTemplate;
}

/** A draft rule suggested by the model — no id yet, nothing persisted (mirrors the admin app's
 * CreateWithAiDialog, minus form-field generation — the add-in's quick-onboarding flow only
 * drafts/imports rules, keeping the narrow task pane's review step to a single short list). */
export interface AiGeneratedRule {
  title: string;
  section?: string;
  description?: string;
  severityGuidance?: RuleSeverity;
}

/** Sends a free-text prompt to the same LLM used for review judgment and gets back draft QC
 * rules (and form fields, which the add-in's quick-onboarding flow ignores). Nothing is
 * persisted — call importQcTemplateRules with the rows to actually create them. */
export async function generateQcTemplateRules(templateId: string, prompt: string): Promise<AiGeneratedRule[]> {
  const res = await authedFetch(`/api/admin/qc-templates/${templateId}/ai-generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt }),
  });
  if (!res.ok) throw await toApiError(res, 'Failed to generate rules');
  const data = (await res.json()) as { rules: AiGeneratedRule[] };
  return data.rules;
}

/** Atomically creates the given AI-drafted rules for this template (additive). */
export async function importQcTemplateRules(templateId: string, rules: AiGeneratedRule[]): Promise<QcTemplateRule[]> {
  const res = await authedFetch(`/api/admin/qc-templates/${templateId}/ai-import`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rules, formFields: [] }),
  });
  if (!res.ok) throw await toApiError(res, 'Failed to save generated rules');
  const data = (await res.json()) as { rules: QcTemplateRule[] };
  return data.rules;
}

/** Single-template detail (e.g. `aiSuggestionsEnabled`) — used to gate the "Hide AI suggestions"
 * control on the template's own setting rather than on whether any ai_suggested issue happens to
 * have been found yet in the current run. */
export async function fetchQcTemplate(templateId: string): Promise<QcTemplate | null> {
  const res = await authedFetch(`/api/admin/qc-templates/${templateId}`);
  if (!res.ok) return null;
  const data = (await res.json()) as { qcTemplate: QcTemplate };
  return data.qcTemplate;
}

export async function fetchQcTemplateForm(templateId: string): Promise<{ form: Form | null; fields: FormField[] }> {
  const res = await authedFetch(`/api/admin/qc-templates/${templateId}/form`);
  // Best-effort: template custom fields are supplementary, not required to start a run — a fetch
  // failure here shouldn't block the reviewer, just show no extra fields. The backend has already
  // returned a 200 for the ordinary "no form yet" case, so a non-ok here is a genuine failure.
  if (!res.ok) return { form: null, fields: [] };
  return res.json();
}

/** Read-only rule list for the "Template QC Rules" dialog — same access level as starting a run
 * against this template (view/use, not owner-only). */
export async function fetchQcTemplateRules(templateId: string): Promise<QcTemplateRule[]> {
  const res = await authedFetch(`/api/admin/qc-templates/${templateId}/rules`);
  if (!res.ok) throw new Error(`Failed to fetch template rules: ${res.status}`);
  const data = (await res.json()) as { rules: QcTemplateRule[] };
  return data.rules;
}

/** Reviewer's "I think this should be a rule" idea, for the template owner to approve/reject in
 * the admin's Suggestions tab — see admin/qcTemplatesRoutes.ts's rule-suggestions routes. */
export async function submitRuleSuggestion(templateId: string, suggestionText: string): Promise<void> {
  const res = await authedFetch(`/api/admin/qc-templates/${templateId}/rule-suggestions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ suggestionText }),
  });
  if (!res.ok) throw await toApiError(res, 'Failed to submit rule suggestion');
}

export interface ReviewProgress {
  phase: 'queued' | 'deterministic' | 'judgment' | 'writing_comments' | 'done' | 'failed' | 'cancelled';
  /** Units reviewed so far / in total — slides for a deck, pages (or sections) for a Word document. */
  slidesProcessed: number;
  totalSlides: number;
  issuesFoundSoFar: number;
  documentType?: DocumentType;
  unitLabel?: UnitLabel;
  /** Word documents only: first paragraph index of each page/section, keyed by its number. */
  unitAnchors?: Record<string, number>;
  /** Per-step outcome; absent on runs recorded before retry support, which can't be retried. */
  passes?: Partial<Record<ReviewPassName, ReviewPassState>>;
  error?: string;
  /** Non-fatal informational note (e.g. some slides' comments were skipped) — distinct from `error`. */
  warning?: string;
}

/** Whether the run has stopped for good unless someone retries it. */
export function isTerminalPhase(phase: ReviewProgress['phase']): boolean {
  return phase === 'done' || phase === 'failed' || phase === 'cancelled';
}

/** Steps of a stopped run that didn't finish, i.e. what a retry would redo. */
export function retryablePasses(progress: ReviewProgress): ReviewPassName[] {
  return REVIEW_PASSES.filter((name) => {
    const status = progress.passes?.[name]?.status;
    return status === 'failed' || status === 'cancelled' || status === 'pending' || status === 'running';
  });
}

/** Stops a run that is still processing. Everything found so far is kept. */
export async function cancelReview(runId: string): Promise<void> {
  const res = await authedFetch(`/api/reviews/${runId}/cancel`, { method: 'POST' });
  if (!res.ok) throw await toApiError(res, 'Failed to cancel the run');
}

/** Re-runs only the steps of a stopped run that didn't finish, keeping everything already found. */
export async function retryReview(runId: string): Promise<void> {
  const res = await authedFetch(`/api/reviews/${runId}/retry`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  if (!res.ok) throw await toApiError(res, 'Failed to retry the run');
}

export async function createReview(
  fileBytes: Uint8Array,
  fileName: string,
  projectContext: ProjectContext,
  selection?: { projectId?: string; qcTemplateId?: string; formResponses?: FormResponses; adHocRules?: AdHocRuleInput[]; documentKey?: string },
): Promise<string> {
  const form = new FormData();
  const arrayBuffer = fileBytes.buffer.slice(fileBytes.byteOffset, fileBytes.byteOffset + fileBytes.byteLength) as ArrayBuffer;
  form.append('file', new Blob([arrayBuffer]), fileName);
  form.append('projectContext', JSON.stringify(projectContext));
  if (selection?.documentKey) form.append('documentKey', selection.documentKey);
  if (selection?.projectId) form.append('projectId', selection.projectId);
  if (selection?.qcTemplateId) form.append('qcTemplateId', selection.qcTemplateId);
  if (selection?.formResponses) form.append('formResponses', JSON.stringify(selection.formResponses));
  if (selection?.adHocRules && selection.adHocRules.length > 0) form.append('adHocRules', JSON.stringify(selection.adHocRules));

  const res = await authedFetch('/api/reviews', { method: 'POST', body: form });
  if (!res.ok) throw await toApiError(res, 'Failed to start review');
  const data = (await res.json()) as { runId: string };
  return data.runId;
}

/** Lightweight prior-run summary, for the SCOPE CONFIRMATION step (new pass vs. continuation). */
export interface PriorRun {
  id: string;
  progress: ReviewProgress;
  issueCount: number;
  createdAt: string;
}

/** Prior runs against this same filename, newest first — used to prompt "N previous run(s) found". */
export async function fetchPriorRuns(fileName: string): Promise<PriorRun[]> {
  const res = await authedFetch(`/api/reviews?fileName=${encodeURIComponent(fileName)}`);
  if (!res.ok) return [];
  const data = (await res.json()) as { runs: PriorRun[] };
  return data.runs;
}

/** The native EventSource API can't set an Authorization header, so the access token is passed as
 * a query param instead (the backend's requireUser middleware accepts either — see authz.ts). */
export function subscribeToProgress(runId: string, onUpdate: (progress: ReviewProgress) => void): () => void {
  let source: EventSource | undefined;
  let closed = false;
  void getAccessToken().then((token) => {
    if (closed || !token) return;
    source = new EventSource(`${API_BASE}/api/reviews/${runId}/events?access_token=${encodeURIComponent(token)}`);
    source.onmessage = (event) => onUpdate(JSON.parse(event.data));
  });
  return () => {
    closed = true;
    source?.close();
  };
}

/** One-shot status fetch (vs. the live `subscribeToProgress` stream) — used to rehydrate after the taskpane remounts. */
export async function fetchStatus(runId: string): Promise<ReviewProgress> {
  const res = await authedFetch(`/api/reviews/${runId}/status`);
  if (!res.ok) throw new Error(`Failed to fetch status: ${res.status}`);
  const data = (await res.json()) as { progress: ReviewProgress };
  return data.progress;
}

export async function fetchIssues(runId: string): Promise<Issue[]> {
  const res = await authedFetch(`/api/reviews/${runId}/issues`);
  if (!res.ok) throw new Error(`Failed to fetch issues: ${res.status}`);
  const data = (await res.json()) as { issues: Issue[] };
  return data.issues;
}

/** The rule snapshot (template rules + any ad-hoc rules) a run actually used — fetched once a run
 * reaches 'done'/'failed', not polled. Looked up by `Issue.controlId` to show which rule an
 * issue's info-icon tooltip is about (see IssueList.tsx). */
export interface ResolvedRuleSummary {
  controlId: string;
  section: string | null;
  title: string;
  severityGuidance: string | null;
}

export async function fetchResolvedRules(runId: string): Promise<ResolvedRuleSummary[]> {
  const res = await authedFetch(`/api/reviews/${runId}/rules`);
  if (!res.ok) return [];
  const data = (await res.json()) as { rules: ResolvedRuleSummary[] };
  return data.rules;
}

/** Thumbs up/down (+ optional free-text comment) on one issue — shown in IssueList. Feeds the
 * backend's AI-suggestion clustering (recurring issues become candidate rules). Resubmitting with
 * a different rating updates the reviewer's own feedback rather than adding a duplicate. */
export async function submitIssueFeedback(runId: string, issueId: string, input: { rating: 'up' | 'down'; comment?: string }): Promise<void> {
  const res = await authedFetch(`/api/reviews/${runId}/issues/${encodeURIComponent(issueId)}/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await toApiError(res, 'Failed to submit feedback');
}

/** Records who resolved or reopened an issue and how. 'auto' = QC Bot edited the document;
 * 'manual' = a person fixed it by hand. Reopening ('Fail') clears the resolution. */
export async function updateIssueStatus(
  runId: string,
  issueId: string,
  status: 'Fixed' | 'Fail',
  method?: ResolutionMethod,
): Promise<{ status: 'Fixed' | 'Fail'; resolution: IssueResolution | null }> {
  const res = await authedFetch(`/api/reviews/${runId}/issues/${encodeURIComponent(issueId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status, method }),
  });
  if (!res.ok) throw await toApiError(res, 'Failed to update issue status');
  return res.json();
}

// ---------------------------------------------------------------------------
// Run-level collaboration
// ---------------------------------------------------------------------------

/** Runs others invited the caller to, newest first; narrowed to the open document when `fileName` is given. */
export async function fetchSharedRuns(fileName?: string, documentKey?: string): Promise<SharedRunSummary[]> {
  const params = new URLSearchParams();
  if (fileName) params.set('fileName', fileName);
  if (documentKey) params.set('documentKey', documentKey);
  const res = await authedFetch(`/api/reviews/shared?${params}`);
  if (!res.ok) return [];
  const data = (await res.json()) as { runs: SharedRunSummary[] };
  return data.runs;
}

export async function fetchCollab(runId: string): Promise<RunCollabSnapshot> {
  const res = await authedFetch(`/api/reviews/${runId}/collaborators`);
  if (!res.ok) throw await toApiError(res, 'Failed to load collaborators');
  return res.json();
}

export async function inviteCollaborator(runId: string, email: string): Promise<RunParticipant> {
  const res = await authedFetch(`/api/reviews/${runId}/collaborators`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw await toApiError(res, 'Failed to add collaborator');
  const data = (await res.json()) as { participant: RunParticipant };
  return data.participant;
}

export async function removeCollaborator(runId: string, userId: string): Promise<void> {
  const res = await authedFetch(`/api/reviews/${runId}/collaborators/${encodeURIComponent(userId)}`, { method: 'DELETE' });
  if (!res.ok) throw await toApiError(res, 'Failed to remove collaborator');
}

/** Heartbeat and soft claim in one: `activeIssueId` is the issue being worked on now, or null. */
export async function setPresence(runId: string, activeIssueId: string | null): Promise<void> {
  const res = await authedFetch(`/api/reviews/${runId}/presence`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ activeIssueId }),
  });
  if (!res.ok) throw await toApiError(res, 'Failed to update presence');
}

export async function searchUsers(query: string): Promise<UserSearchResult[]> {
  const res = await authedFetch(`/api/admin/users/search?q=${encodeURIComponent(query)}`);
  if (!res.ok) throw await toApiError(res, 'Search failed');
  const data = (await res.json()) as { users: UserSearchResult[] };
  return data.users;
}

export async function fetchMyUserId(): Promise<string> {
  const res = await authedFetch('/api/admin/me');
  if (!res.ok) throw await toApiError(res, 'Failed to load profile');
  const data = (await res.json()) as { id: string };
  return data.id;
}

/** Live run events over SSE. Reconnects with a fresh token on any drop, because the query-param
 * token the browser would replay on its own reconnect may have expired by then. */
export function subscribeToRunLive(runId: string, onEvent: (event: RunLiveEvent) => void): () => void {
  let source: EventSource | undefined;
  let closed = false;
  let retryMs = 1000;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const scheduleRetry = () => {
    if (closed) return;
    timer = setTimeout(() => void open(), retryMs);
    retryMs = Math.min(retryMs * 2, 30_000);
  };

  async function open() {
    const token = await getAccessToken();
    if (closed) return;
    if (!token) {
      scheduleRetry();
      return;
    }
    source = new EventSource(`${API_BASE}/api/reviews/${runId}/live?access_token=${encodeURIComponent(token)}`);
    source.onmessage = (message) => {
      retryMs = 1000;
      try {
        onEvent(JSON.parse(message.data) as RunLiveEvent);
      } catch {
        // A malformed frame is skipped; the next snapshot/resync restores state.
      }
    };
    source.onerror = () => {
      source?.close();
      scheduleRetry();
    };
  }

  void open();
  return () => {
    closed = true;
    clearTimeout(timer);
    source?.close();
  };
}

/** Downloads via a blob + temporary object URL (rather than a plain <a href>, which can't carry
 * the bearer token) — mirrors the admin app's downloadAnnotatedDeck. */
export async function downloadAnnotatedDeck(runId: string, fileName: string): Promise<void> {
  const res = await authedFetch(`/api/reviews/${runId}/download`);
  if (!res.ok) throw await toApiError(res, 'Download failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `QC-${fileName}`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function askChat(runId: string, question: string): Promise<string> {
  const res = await authedFetch(`/api/reviews/${runId}/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ question }),
  });
  if (!res.ok) throw await toApiError(res, 'Chat request failed');
  const data = (await res.json()) as { answer: string };
  return data.answer;
}

